import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { EstimativaCusto } from 'helper/windx';
import {
  selectWindxTelaAtiva,
  selectWindxGranularidadeSH,
  selectWindxIdSubdivisaoEspacial,
  selectWindxIdsEntidades,
  selectWindxIdsModelos,
  selectWindxOpcoesRmv,
  selectWindxIdsSimulacoesAntigasAtivas,
  selectWindxFatorRen,
  selectWindxPeriodoEixoX,
} from 'redux/reducers/windxControlPanelReducer';
import { useLazyGetConsultaEstimativaQuery } from 'services/api/windx';

const DEBOUNCE_MS = 300;

/**
 * Medidor de complexidade (WINDX-051). A cada mudança de seleção (debounce
 * ~300ms) consulta o pré-voo `/consulta/estimativa` e devolve a estimativa de
 * custo (séries/pontos/payload/veredito/sugestões) — SEM rodar a query pesada.
 *
 * Escopo: WEOL-SH (gráfico de combinação, onde mora a explosão combinatória).
 * Nas demais telas devolve `undefined` (sem medidor). O período (data_inicio/
 * data_fim) usa o default derivado de 14 dias — é uma estimativa; o que domina
 * o veredito é o nº de séries, não o ajuste fino de pontos.
 */
export default function useEstimativaWindx(): {
  estimativa: EstimativaCusto | undefined;
  isFetching: boolean;
} {
  const telaAtiva = useSelector(selectWindxTelaAtiva);
  const granularidade = useSelector(selectWindxGranularidadeSH);
  const idSubdivisao = useSelector(selectWindxIdSubdivisaoEspacial);
  const idsEntidades = useSelector(selectWindxIdsEntidades);
  const idsModelos = useSelector(selectWindxIdsModelos);
  const opcoesRmv = useSelector(selectWindxOpcoesRmv);
  const idsSimAntigas = useSelector(selectWindxIdsSimulacoesAntigasAtivas);
  const fatorRen = useSelector(selectWindxFatorRen);

  const [trigger, { data, isFetching }] = useLazyGetConsultaEstimativaQuery();

  // Só faz sentido em WEOL-SH. Seleção minimamente avaliável: subdivisão +
  // ≥1 entidade + ≥1 modelo (senão não há o que estimar).
  const selecaoAvaliavel = (
    telaAtiva === 'sh'
    && typeof idSubdivisao === 'number'
    && idsEntidades.length > 0
    && idsModelos.length > 0
  );

  useEffect(() => {
    if (!selecaoAvaliavel || typeof idSubdivisao !== 'number') {
      return undefined;
    }
    const { dataInicio, dataFim } = selectWindxPeriodoEixoX();
    const handle = setTimeout(() => {
      trigger({
        frequencia_temporal: granularidade === 'diario' ? 'd' : 'sh',
        subdivisao_espacial: idSubdivisao,
        id_entidades: idsEntidades,
        id_modelos: idsModelos,
        opcoes_rmv: opcoesRmv,
        data_inicio: dataInicio,
        data_fim: dataFim,
        n_simulacoes_antigas: idsSimAntigas.length,
        incluir_fator_ren: fatorRen,
      });
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [
    selecaoAvaliavel,
    idSubdivisao,
    idsEntidades,
    idsModelos,
    opcoesRmv,
    idsSimAntigas,
    fatorRen,
    granularidade,
    trigger,
  ]);

  if (!selecaoAvaliavel) {
    return { estimativa: undefined, isFetching: false };
  }
  return { estimativa: data, isFetching };
}
